<template>
    <div>
        <LoadingComponent :props="loading" />
        <div class="w-full mb-6">
        <h4 class="font-medium text-xl mb-3 text-heading leading-5">{{ $t("menu.overview") }}</h4>
        
        <!-- Tarjetas Principales (4 Visibles - Reordenables con Draggable) -->
        <draggable v-model="mainCardsList" item-key="key" class="row" :disabled="!isReorderModeActive" @change="saveCardsOrder">
            <template #item="{ element: card }">
                <div class="col-6 sm:col-6 xl:col-3 mb-0">
                    <div class="bg-white p-4 rounded-lg border flex items-center justify-between gap-2 shadow-sm hover:shadow transition-shadow relative"
                        :class="{ 'cursor-move ring-2 ring-primary/40 bg-gray-50/50': isReorderModeActive }">
                        <div class="flex items-center gap-2">
                            <div class="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-white">
                                <i :class="card.icon" class="text-heading text-lg sm:text-2xl bg-gray-100 p-2 sm:p-2.5 rounded-full"></i>
                            </div>
                            <div class="pl-3 py-1 border-l h-18 sm:h-auto">
                                <h3 class="font-medium text-heading text-xs">{{ $t(card.label) }}</h3>
                                <h4 class="font-medium font-numbers text-2xl leading-8 text-heading">{{ formatValue(card) }}</h4>
                            </div>
                        </div>
                        <i v-if="isReorderModeActive" class="lab-line-menu text-gray-400 text-lg cursor-move flex-shrink-0" title="Arrastrar para reordenar"></i>
                    </div>
                </div>
            </template>
        </draggable>

        <!-- Bloque Colapsable (Tarjetas Restantes) con animación SlideIn/Out y Draggable -->
        <Transition name="slide-fade">
            <div v-show="showMoreCards" class="mt-3">
                <draggable v-model="secCardsList" item-key="key" class="row" :disabled="!isReorderModeActive" @change="saveCardsOrder">
                    <template #item="{ element: card }">
                        <div class="col-12 sm:col-6 xl:col-3">
                            <div class="bg-white p-4 rounded-lg border flex items-center justify-between gap-2 shadow-sm hover:shadow transition-shadow relative"
                                :class="{ 'cursor-move ring-2 ring-primary/40 bg-gray-50/50': isReorderModeActive }">
                                <div class="flex items-center gap-2">
                                    <div class="w-12 h-12 flex items-center justify-center bg-white">
                                        <i :class="card.icon" class="text-heading text-2xl lab-font-size-24 bg-gray-100 p-2.5 rounded-full"></i>
                                    </div>
                                    <div class="pl-3 py-1 border-l">
                                        <h3 class="font-medium text-heading text-xs">{{ $t(card.label) }}</h3>
                                        <h4 class="font-medium font-numbers text-2xl leading-8 text-heading">{{ formatValue(card) }}</h4>
                                    </div>
                                </div>
                                <i v-if="isReorderModeActive" class="lab-line-menu text-gray-400 text-lg cursor-move flex-shrink-0" title="Arrastrar para reordenar"></i>
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </Transition>

        <!-- Línea de separación con botón 'Ver más indicadores' sobrepuesto -->
        <div class="relative my-6">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-gray-200"></div>
            </div>
            <div class="relative flex justify-center">
                <button type="button" @click="showMoreCards = !showMoreCards"
                    class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none transition-all">
                    <span>{{ showMoreCards ? $t('label.see_less_kpis') : $t('label.see_more_kpis') }}</span>
                    <i :class="showMoreCards ? 'lab-line-chevron-up' : 'lab-line-chevron-down'" class="text-xs"></i>
                </button>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
import LoadingComponent from "../components/LoadingComponent";
import draggable from "vuedraggable";

export default {
    name: "OverviewComponent",
    components: { LoadingComponent, draggable },
    emits: ['activity'],
    props: {
        isReorderModeActive: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            showMoreCards: false,
            aiImportEnabled: false,
            total_sales: null,
            total_orders: null,
            total_customers: null,
            total_products: null,
            commerce_kpis: {},
            mainCardsList: [
                { key: 'orders_today', label: 'label.orders_today', icon: 'lab-fill-box' },
                { key: 'pending_orders', label: 'label.pending_orders_total', icon: 'lab-fill-document' },
                { key: 'ongoing_orders', label: 'label.ongoing', icon: 'lab-fill-shipping' },
                { key: 'low_stock_products', label: 'label.low_stock_products', icon: 'lab-fill-box-search' },
            ],
            secCardsList: [
                { key: 'total_sales', label: 'label.total_earnings', icon: 'lab-fill-dollar-circle' },
                { key: 'total_products', label: 'label.total_products', icon: 'lab-fill-document' },
                { key: 'total_customers', label: 'label.total_customers', icon: 'lab-fill-users' },
                { key: 'total_orders', label: 'label.total_orders', icon: 'lab-fill-box' },
                { key: 'average_order_value', label: 'label.average_order_value', icon: 'lab-fill-dollar-circle' },
                { key: 'active_customers_30d', label: 'label.active_customers_30d', icon: 'lab-fill-users' },
                { key: 'repeat_customer_rate', label: 'label.repeat_customer_rate', icon: 'lab-fill-users' },
                { key: 'shipment_success_rate', label: 'label.shipment_success_rate', icon: 'lab-fill-box' },
                { key: 'ai_import_success_rate', label: 'label.ai_import_success_rate', icon: 'lab-fill-box' },
            ]
        };
    },
    mounted() {
        this.totalSales();
        this.totalOrders();
        this.totalCustomers();
        this.totalProducts();
        this.commerceKpis();
        this.fetchAiImportSetting();
        this.loadSavedCardsOrder();
    },
    methods: {
        fetchAiImportSetting: function () {
            this.$store.dispatch('aiImportSetting/importSetting').then((res) => {
                this.aiImportEnabled = !!res?.data?.data?.is_enabled;
                this.filterSecondaryCards();
            }).catch(() => {
                this.aiImportEnabled = false;
                this.filterSecondaryCards();
            });
        },
        filterSecondaryCards: function () {
            if (!this.aiImportEnabled) {
                this.secCardsList = this.secCardsList.filter(c => c.key !== 'ai_import_success_rate');
            }
        },
        loadSavedCardsOrder: function () {
            try {
                const savedMain = localStorage.getItem('admin_dashboard_main_cards_order');
                if (savedMain) {
                    const keys = JSON.parse(savedMain);
                    const map = new Map(this.mainCardsList.map(c => [c.key, c]));
                    const ordered = keys.map(k => map.get(k)).filter(Boolean);
                    this.mainCardsList.forEach(c => { if (!ordered.includes(c)) ordered.push(c); });
                    this.mainCardsList = ordered;
                }
                const savedSec = localStorage.getItem('admin_dashboard_sec_cards_order');
                if (savedSec) {
                    const keys = JSON.parse(savedSec);
                    const map = new Map(this.secCardsList.map(c => [c.key, c]));
                    const ordered = keys.map(k => map.get(k)).filter(Boolean);
                    this.secCardsList.forEach(c => { if (!ordered.includes(c)) ordered.push(c); });
                    this.secCardsList = ordered;
                }
            } catch (e) {
                console.error("Error loading cards order", e);
            }
        },
        saveCardsOrder: function () {
            try {
                const mainKeys = this.mainCardsList.map(c => c.key);
                const secKeys = this.secCardsList.map(c => c.key);
                localStorage.setItem('admin_dashboard_main_cards_order', JSON.stringify(mainKeys));
                localStorage.setItem('admin_dashboard_sec_cards_order', JSON.stringify(secKeys));
                this.$emit('activity');
            } catch (e) {
                console.error("Error saving cards order", e);
            }
        },
        totalSales: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/totalSales").then((res) => {
                this.total_sales = res.data.data.total_sales;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        totalOrders: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/totalOrders").then((res) => {
                this.total_orders = res.data.data.total_orders;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        totalCustomers: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/totalCustomers").then((res) => {
                this.total_customers = res.data.data.total_customers;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        totalProducts: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/totalProducts").then((res) => {
                this.total_products = res.data.data.total_products;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        },
        commerceKpis: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/commerceKpis").then((res) => {
                this.commerce_kpis = res.data.data || {};
                this.loading.isActive = false;
            }).catch(() => {
                this.loading.isActive = false;
            });
        },
        formatValue: function (card) {
            if (card.key === 'total_sales') return this.total_sales ?? '-';
            if (card.key === 'total_products') return this.total_products ?? '-';
            if (card.key === 'total_customers') return this.total_customers ?? '-';
            if (card.key === 'total_orders') return this.total_orders ?? '-';

            const value = this.commerce_kpis?.[card.key];
            if (value === null || typeof value === 'undefined') return '-';
            if (typeof value === 'number' && card.key.endsWith('_rate')) return `${value}%`;
            return value;
        }
    },
}
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 0.35s ease-out;
}
.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}
</style>